import { LitElement, html, css, PropertyValues } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import type { EChartsOption, ECharts } from 'echarts';
import * as echarts from 'echarts';
import { TitleOption } from 'echarts/types/dist/shared';
import { defaults, configure, setData } from './vxc_chart.def'

const parseString = (str: string) : string|Array<string>|object => {
    var resp = str 
    try { resp = JSON.parse(str) } catch(e) {}
    return resp
}

/**
 * Custom web component for Apache ECharts
 * @element echarts-chart
 * @fires chartClick - Fires when chart is clicked
 * @fires chartInit - Fires when chart is initialized
 * @fires chartDestroy - Fires when chart is destroyed
 */
@customElement('vxc-chart')
export class TChart extends LitElement {
    // Static styles
    static styles = css`
        :host {
            display: block;
            width: 100%;
            height: 100%;
        }
        .chart-container {
            width: 100%;
            height: 100%;
            min-height: 200px;
        }
    `;

    // Properties
    @property({ type: String , attribute: 'theme'   })  theme?: string;
    @property({ type: String , attribute: 'render'  })  renderer: 'canvas' | 'svg' = 'canvas';
	@property({ type: String, attribute: 'source' 	})  source: string = '';
    @property({ type: Number, attribute: 'refresh' 	})  refresh: number = 0;
    @property({ type: Boolean, attribute: false     })  loading = false;
    @property({ type: Object , attribute: false     })  loadingOptions?: object;

    // ResizeObserver instance
    private resizeObserver?: ResizeObserver;
    
    // chart options
    @property({ 
        type: Object , 
        attribute: 'settings' 
    }) options: EChartsOption = defaults.options as EChartsOption;

    // chart type
    @property({ 
        type: String,
        noAccessor: true,
        hasChanged: () => false,
        reflect: true,
        attribute: 'type' 
    }) _type: string = '';
    
    get type() { return this._type }
    set type(value: string) {
        this._type = value
        this.options = configure(value) as EChartsOption
        console.log('set type', value, this.options)
    }

    // chart title
    @property({ 
        type: String,
        noAccessor: true,
        hasChanged: () => false,
        reflect: true,
        attribute: 'title' 
    }) _title: string = '';
    
    get title() { return this._title }
    set title(value: string) {
        this._title = value
        let title: TitleOption = this.options.title as TitleOption ||  defaults.title
        title.text = value 
        this.options = { ...this.options, title }
        console.log('set title', value, this.options)
    }

    // chart sub-title
    @property({ 
        type: String,
        noAccessor: true,
        hasChanged: () => false,
        reflect: true,
        attribute: 'sub-title' 
    }) _subtitle: string = '';
    
    get subtitle() { return this._subtitle }
    set subtitle(value: string) {
        this._subtitle = value
        let title: TitleOption = this.options.title as TitleOption ||  defaults.title
        title.subtext = value 
        this.options = { ...this.options, title }
        console.log('set sub-title', value, this.options)
    }

    // chart x-axis
    @property({ 
        type: String,
        noAccessor: true,
        hasChanged: () => false,
        reflect: true,
        attribute: 'x-axis' 
    }) _xaxis: string = '';
    
    get xaxis() { return this._xaxis }
    set xaxis(value: string) {
        this._xaxis = value
        let xAxis: echarts.XAXisComponentOption = this.options.xAxis as echarts.XAXisComponentOption ||  defaults.xAxis
        let axis = parseString(value)
        xAxis.type = Array.isArray(axis) ? 'value' : axis as "value" | "category" | "time" | "log" | undefined
        // @ts-ignore
        xAxis.data = Array.isArray(axis)? axis : []
        this.options = { ...this.options, xAxis }
        console.log('set x-axis', value, this.options)
    }

    // chart y-axis
    @property({ 
        type: String,
        noAccessor: true,
        hasChanged: () => false,
        reflect: true,
        attribute: 'y-axis' 
    }) _yaxis: string = '';
    
    get yaxis() { return this._yaxis }
    set yaxis(value: string) {
        this._yaxis = value
        let yAxis: echarts.YAXisComponentOption = this.options.yAxis as echarts.YAXisComponentOption ||  defaults.yAxis
        let axis = parseString(value)
        yAxis.type = Array.isArray(axis) ? 'value' : axis as "value" | "category" | "time" | "log" | undefined
        // @ts-ignore
        yAxis.data = Array.isArray(axis)? axis : []
        this.options = { ...this.options, yAxis }
        console.log('set y-axis', value, this.options)
    }

    // chart x-axis-name
    @property({ 
        type: String,
        noAccessor: true,
        hasChanged: () => false,
        reflect: true,
        attribute: 'x-axis-name' 
    }) _xaxisname: string = '';
    
    get xaxisname() { return this._xaxisname }
    set xaxisname(value: string) {
        this._xaxisname = value
        let xAxis: echarts.XAXisComponentOption = this.options.xAxis as echarts.XAXisComponentOption ||  defaults.xAxis
        xAxis.name = value
        this.options = { ...this.options, xAxis }
        console.log('set x-axis-name', value, this.options)
    }

    // chart y-axis-name
    @property({ 
        type: String,
        noAccessor: true,
        hasChanged: () => false,
        reflect: true,
        attribute: 'y-axis-name' 
    }) _yaxisname: string = '';
    
    get yaxisname() { return this._yaxisname }
    set yaxisname(value: string) {
        this._yaxisname = value
        let yAxis: echarts.YAXisComponentOption = this.options.yAxis as echarts.YAXisComponentOption ||  defaults.yAxis
        yAxis.name = value
        this.options = { ...this.options, yAxis }
        console.log('set y-axis-name', value, this.options)
    }

    // chart axis for radar 
    @property({ 
        type: String,
        noAccessor: true,
        hasChanged: () => false,
        reflect: true,
        attribute: 'axis' 
    }) _axis: string = '';
    
    get axis() { return this._axis }
    set axis(value: string) {
        this._axis = value
        let radar: echarts.RadarComponentOption = this.options.radar as echarts.RadarComponentOption ||  defaults.radarAxis
        let axis: echarts.RadarIndicatorOption = parseString(value) as echarts.RadarIndicatorOption     
        radar.indicator = Object.keys(axis).map( (key:string) => ( { name: key, max: (axis as any)[key]} ))
        this.options = { ...this.options, radar }
        console.log('set axis', value, this.options)
    }

    // chart data
    @property({ 
        type: String,
        noAccessor: true,
        hasChanged: () => false,
        reflect: true,
        attribute: 'data' 
    }) _data: string = '';
    
    get data() { return this._data }
    set data(value: string) {
        this._data= value
        this.options = setData(JSON.parse(value), this.options) as EChartsOption
        console.log('set data', value, this.options)
    }

    // Internal state
    @state() private _chartInstance?: ECharts;

    // DOM reference
    @query('.chart-container') private chartContainer!: HTMLDivElement;

    attributeChangedCallback(name: string, oldValue: any, newValue: any) {
        super.attributeChangedCallback(name, oldValue, newValue);
        console.log('Attribute changed:', newValue);
        switch(name) {
            case 'type':
                this.type = newValue;
                break
            case 'title':
                this.title = newValue;
                break;
            case 'sub-title':
                this.subtitle = newValue;
                break;
            case 'data':
                this.data = newValue;
                break;
            case 'axis':
                this.axis = newValue;
                break;
            case 'x-axis':
                this.xaxis = newValue;
                break
            case 'y-axis':
                this.yaxis = newValue;
                break
            case 'x-axis-name':
                this.xaxisname = newValue;
                break
            case 'y-axis-name':
                this.xaxisname = newValue;
                break
            }
    }

    private  abortController: AbortController | null = null;
    
    private cancelCurrentFetch() {
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
    }
  
    private setData(data: Array<Object>) {
        var changed: boolean = true
        try {
            console.log('setData', data)
            if(data)        this.options = setData(data, {...this.options}) as EChartsOption
            else            console.log('no data change')
        }
        catch(e) { console.error(e) }
        finally  { return changed }
    }
      
    private async fetchData() {
        console.log('fetchData()', this.source)
        if (!this.source?.length) return;
  
        // Cancel any ongoing fetch before starting a new one
        this.cancelCurrentFetch();
  
        // Create new AbortController for this fetch
        this.abortController = new AbortController();
  
        try {
            const response = await fetch(this.source, {
                signal: this.abortController.signal,
                headers: {
                    'x-api-key': 'api_key',
                    'Content-Type': 'application/json',
                }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const jsonData = await response.json();
            console.log('fetch returned :', jsonData)
            let data = jsonData || {}
            this.setData(data)            
        } 
        catch (e) { 
            console.error((e as Error).name, (e as Error).message) 
        }
        finally {
            if (this.abortController) {  // Only update loading state if not aborted
                this.abortController = null;
                if(this.refresh)
                    setTimeout(() => this.fetchData(), this.refresh)
            }
        }
    }

    // Lifecycle methods
    connectedCallback() {
        super.connectedCallback();
        // Set up resize observer
        this.resizeObserver = new ResizeObserver(() => this.resizeChart())
    }

    firstUpdated() {
        // initialize the chart
        this.initChart()
    }

    disconnectedCallback() {
        super.disconnectedCallback()
        this.cancelCurrentFetch()
        this.destroyChart()
    }

    updated(changedProperties: PropertyValues) {
        super.updated(changedProperties);

        if (changedProperties.has('options') && this._chartInstance) {
            this.updateChartOptions();
        }

        if (changedProperties.has('loading')) {
            this.toggleLoading();
        }

        if (changedProperties.has('source') && this.source) {
            this.fetchData();
        }
    }

    // Chart initialization
    private initChart() {
        console.log('initChart', this.chartContainer)
        if (!this.chartContainer) return;

        // Initialize ECharts instance
        this._chartInstance = echarts.init(this.chartContainer)

        // Set initial options
        this.updateChartOptions();

        // Add event listeners
        this.setupEventListeners();

        // Observe size changes
        this.resizeObserver?.observe(this.chartContainer);
  }

    // Update chart options
    private updateChartOptions() {
        if (!this._chartInstance) return;

        try {
            console.log('setOptions', this.options)
            this._chartInstance.setOption(this.options, {
                notMerge: true,
                lazyUpdate: true,
            });
        } 
        catch (error) {
            console.error('Failed to update chart options:', error);
        }
    }

    // Set up event listeners
    private setupEventListeners() {
        if (!this._chartInstance) return;

        this._chartInstance.on('click', (params) => {
        this.dispatchEvent(new CustomEvent('chartClick', {
            detail: params,
            bubbles: true,
            composed: true
        }));
        });

        // Add more event listeners as needed
  }

  // Handle chart resizing
    private resizeChart() {
        if (!this._chartInstance) return;

        try {
            this._chartInstance.resize({
                animation: {
                duration: 300,
                easing: 'cubicOut'
                }
        });
        } 
        catch (error) {
            console.error('Failed to resize chart:', error);
        }
  }

    // Toggle loading state
    private toggleLoading() {
        if (!this._chartInstance) return;

        if (this.loading) {
            this._chartInstance.showLoading('default', this.loadingOptions);
        } 
        else {
            this._chartInstance.hideLoading();
        }
    }

    // Clean up chart instance
    private destroyChart() {
        if (this._chartInstance) {
            this.resizeObserver?.unobserve(this.chartContainer);
            this._chartInstance.dispose();
            this.dispatchEvent(new CustomEvent('chartDestroy'));
        }
    }

    // Expose chart instance method
    public getChartInstance(): ECharts | undefined {
        return this._chartInstance;
    }

    // Template render
    render() {
        return html`
        <div class="chart-container"></div>
        `;
    }
}

// Type declarations
declare global {
    interface HTMLElementTagNameMap {
        'vxc-chart': TChart;
    }
}